import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import QuestionView from '../../../../Admin/Question/View/QuestionView';

export default function QuestionSummary({ answer, idx, fontSize = 18, onOpenQuestionModal, exam = {}, hideLevel = false, ViewGameResult = false }) {
    const { t } = useTranslation('common');
    const pct = answer.maxMark > 0 ? Math.round(((answer.userMark ?? 0) / answer.maxMark) * 100) : 0;
    const [showCorrectAnswer, setShowCorrectAnswer] = useState(false);
    const isUnanswered = answer.isUnanswered || !answer.hasAnswer;
    const levelKey = answer.levelKey || 'easy';
    const levelLabel = answer.levelLabel || (levelKey === 'easy' ? 'سهل' : levelKey === 'medium' ? 'متوسط' : 'صعب');

    // Build true exam answers for QuestionView when model answer is requested
    const getTrueExamAnswers = (q) => {
        if (!q) return [];
        const typeId = Number(q.Type?.Id ?? q.Type ?? q.TypeId ?? 0);
        const qAnswers = Array.isArray(q.Answers) ? q.Answers : [];
        const qFills = Array.isArray(q.Fills) ? q.Fills : [];
        const qMatches = Array.isArray(q.Matches) ? q.Matches : [];
        const qCategories = Array.isArray(q.Categories) ? q.Categories : [];

        let trueAnswerData = [];

        // Type 5 (FillListView) & Type 6 (FillChooseView - اختر من قائمة / اختيارات منسدلة)
        if (typeId === 5 || typeId === 6) {
            // 1. From Categories.CustomStyle.Answers
            qCategories.forEach(cat => {
                let catCustom = typeof cat.CustomStyle === 'string' ? JSON.parse(cat.CustomStyle) : (cat.CustomStyle || {});
                if (catCustom && Array.isArray(catCustom.Answers)) {
                    catCustom.Answers.forEach(ans => {
                        trueAnswerData.push({
                            Id: cat.Id,
                            Sort: Number(ans.Sort ?? 0) + 1,
                            Answer: parseInt(ans.Answer ?? ans.AnswerId),
                            isTrue: true
                        });
                    });
                }
            });

            // 2. Fallback from Fills if empty
            if (trueAnswerData.length === 0 && qFills.length > 0) {
                qFills.forEach((f, idx) => {
                    trueAnswerData.push({
                        Id: f.CategoryId || f.Id,
                        Sort: Number(f.Sort ?? idx) <= 0 ? Number(f.Sort ?? idx) + 1 : Number(f.Sort ?? idx),
                        Answer: parseInt(f.AnswerId ?? f.Answer),
                        isTrue: true
                    });
                });
            }
        }
        // Type 3 (FillView - سحب وإفلات في الفراغات)
        else if (typeId === 3) {
            trueAnswerData = qFills.map(f => ({
                ...f,
                isTrue: true
            }));
        }
        // Type 4 (FillTextView - ملء فراغات نصية)
        else if (typeId === 4) {
            qCategories.forEach(cat => {
                let catCustom = typeof cat.CustomStyle === 'string' ? JSON.parse(cat.CustomStyle) : (cat.CustomStyle || {});
                if (catCustom && Array.isArray(catCustom.Answers)) {
                    catCustom.Answers.forEach(ans => {
                        trueAnswerData.push({
                            Id: cat.Id,
                            Sort: Number(ans.Sort ?? 0) + 1,
                            Answer: ans.Answer ?? ans.Value ?? ans.Name,
                            Value: ans.Answer ?? ans.Value ?? ans.Name,
                            isTrue: true
                        });
                    });
                }
            });
            if (trueAnswerData.length === 0 && qFills.length > 0) {
                trueAnswerData = qFills.map((f, idx) => ({
                    Id: f.CategoryId || f.Id,
                    Sort: Number(f.Sort ?? idx) <= 0 ? Number(f.Sort ?? idx) + 1 : Number(f.Sort ?? idx),
                    Answer: f.AnswerId ?? f.Answer ?? f.Name,
                    Value: f.AnswerId ?? f.Answer ?? f.Name,
                    isTrue: true
                }));
            }
        }
        // Type 8 (QuestionnaireView - استبيان / جدول خيارات)
        else if (typeId === 8) {
            qCategories.forEach(cat => {
                let catCustom = typeof cat.CustomStyle === 'string' ? JSON.parse(cat.CustomStyle) : (cat.CustomStyle || {});
                if (catCustom && Array.isArray(catCustom.Answers)) {
                    catCustom.Answers.forEach(ans => {
                        trueAnswerData.push({
                            CategoryId: cat.Id,
                            AnswerId: ans.AnswerId ?? ans.Answer,
                            Sort: ans.Sort
                        });
                    });
                }
            });
            if (trueAnswerData.length === 0 && qFills.length > 0) {
                trueAnswerData = qFills.map(f => ({ ...f }));
            }
        }
        // Type 9 (MatchesView - توصيل / مطابقة)
        else if (typeId === 9) {
            trueAnswerData = qMatches.map(m => ({ ...m }));
        }
        // Type 2 (OrderView - ترتيب)
        else if (typeId === 2) {
            trueAnswerData = [...qAnswers].sort((a, b) => (a.Sort ?? 0) - (b.Sort ?? 0));
        }
        // Game Types (15 to 31)
        else if (typeId >= 15 && typeId <= 31) {
            trueAnswerData = qAnswers.map((a, aIdx) => {
                const catId = parseInt(a.AnswersSelectType || a.CustomStyle?.CategoryId || qCategories[0]?.Id || 0);
                const matchedCat = qCategories.find(c => Number(c.Id) === catId) || qCategories[0] || { Id: catId, Name: '' };
                return {
                    Answer: {
                        Id: a.Id,
                        Sort: a.Sort !== undefined ? a.Sort : aIdx,
                        AnswersSelectType: String(catId),
                        Name: a.Name,
                        ...a
                    },
                    Category: matchedCat,
                    isTrue: true,
                    isIn: true
                };
            });
        }
        // Type 1 (SelectView) & Type 7 (MulitSelectView) & General Choice
        else {
            if (qAnswers.length > 0) {
                trueAnswerData = qAnswers.map(a => ({
                    ...a,
                    isChecked: Boolean(a.True === 1 || a.True === true || a.true === 1 || a.true === true),
                    Value: a.Name
                }));
            } else if (qFills.length > 0) {
                trueAnswerData = qFills.map(f => ({ ...f }));
            } else if (qMatches.length > 0) {
                trueAnswerData = qMatches.map(m => ({ ...m }));
            }
        }

        return [{
            Id: q.Id,
            answers: trueAnswerData,
            mark: q.Mark,
            userMark: q.Mark
        }];
    };

    // User's submitted answers for QuestionView
    const userExamAnswers = (() => {
        if (Array.isArray(exam?.current) && exam.current.length > 0) {
            const hasMatch = exam.current.find(e => e && Number(e.Id) === Number(answer.question.Id));
            if (hasMatch) return exam.current;
        }
        if (answer.userAnswers !== null && answer.userAnswers !== undefined) {
            let uAns = answer.userAnswers;
            if (uAns && typeof uAns === 'object' && !Array.isArray(uAns) && Array.isArray(uAns.answers)) {
                uAns = uAns.answers;
            }
            return [{
                Id: answer.question.Id,
                answers: uAns,
                mark: answer.userMark
            }];
        }
        return [];
    })();

    const activeExamAnswers = showCorrectAnswer ? getTrueExamAnswers(answer.question) : userExamAnswers;
    const typeId = Number(answer?.question?.Type?.Id ?? answer?.question?.Type ?? answer?.question?.TypeId ?? 0);
    const isGameQuestion = (typeId >= 15 && typeId <= 31) || Boolean(answer?.question?.isGame) || Boolean(ViewGameResult);
    const solveOptions = {
        Mark: answer.userMark,
        Score: answer.score || answer.Score || answer.userMark,
        Star: answer.star || answer.Star || (pct === 100 ? 3 : pct >= 50 ? 2 : 1),
        Time: answer.time || answer.Time || 0,
        IsPassed: pct >= 50,
        IsTimesUp: false,
        TryNumber: answer.tryNumber || answer.TryNumber || 1
    };

    return (
        <>
            {/* Question Card Header */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom select-none bg-white">
                <div className="d-flex align-items-center font-weight-bold" style={{ gap: '0.5rem', fontSize: '0.88rem' }}>
                    <span className="d-inline-flex align-items-center" style={{ gap: '0.35rem' }}>
                        <i className="fas fa-question-circle text-warning"></i>
                        <span>{t('Pages.Lessons.Questions.forms.Question') || 'السؤال '} {idx + 1}</span>
                    </span>
                    {!hideLevel && (
                        <span className={`level-label ${levelKey} d-inline-flex align-items-center`} style={{ gap: '0.3rem' }}>
                            <i className={`fas ${levelKey === 'easy' ? 'fa-feather-alt' : levelKey === 'medium' ? 'fa-tachometer-alt' : 'fa-fire-alt'}`}></i>
                            <span>{levelLabel}</span>
                        </span>
                    )}
                </div>

                <div className="d-flex align-items-center" style={{ gap: '0.4rem' }}>
                    {pct !== 100 && (
                        <button
                            type="button"
                            className="theme-btn theme-btn--sm theme-btn--secondary"
                            title={showCorrectAnswer ? (t('materials.question.menu.hideTrueAnswer') || 'إخفاء الإجابة النموذجية') : (t('materials.question.menu.showTrueAnswer') || 'عرض الإجابة النموذجية')}
                            onClick={() => setShowCorrectAnswer(v => !v)}
                        >
                            <i className={`fas ${showCorrectAnswer ? 'fa-eye-slash' : 'fa-eye'} ml-1`}></i>
                            <span>{showCorrectAnswer ? (t('materials.question.menu.hideTrueAnswer') || 'إخفاء الإجابة النموذجية') : (t('materials.question.menu.showTrueAnswer') || 'عرض الإجابة النموذجية')}</span>
                        </button>
                    )}
                    {onOpenQuestionModal && (
                        <button
                            type="button"
                            className="theme-btn theme-btn--sm theme-btn--secondary"
                            title={t('Pages.Exams.SeeResult') || 'عرض السؤال'}
                            onClick={() => onOpenQuestionModal(answer.question)}
                        >
                            <i className="fas fa-expand-alt ml-1"></i>
                            <span>{t('Pages.Exams.SeeResult') || 'عرض'}</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Question Body with QuestionView */}
            <div className="position-relative p-3 select-none bg-white" key={`${answer.question.Id}_${String(showCorrectAnswer)}`}>
                <div className="position-relative">
                    <QuestionView
                        question={answer.question}
                        answers={answer.question.Answers}
                        key={`${answer.question.Id}_${String(showCorrectAnswer)}`}
                        index={idx + 1}
                        indexType={1}
                        showResult={isGameQuestion ? false : true}
                        ViewGameResult={isGameQuestion}
                        onEdit={false}
                        solveButton={false}
                        isExamQuestion={true}
                        saveExamAnswer={() => {}}
                        examAnswers={activeExamAnswers}
                        options={solveOptions}
                        FontSize={fontSize}
                    />
                    {/* Interaction blocker so student cannot change answers */}
                    <div
                        className="interaction-blocker"
                        style={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: 10,
                            pointerEvents: 'auto',
                            background: 'transparent',
                            cursor: 'default'
                        }}
                    />
                </div>

                {/* Question Score & Status Bar */}
                <div className="p-2 border rounded bg-light mt-3">
                    <div className="d-flex align-items-center justify-content-between" style={{ fontSize: '0.82rem' }}>
                        <span className={`font-weight-bold ${pct === 100 ? 'text-success' : pct >= 50 ? 'text-warning' : 'text-danger'}`}>
                            {t('Pages.Exams.Mark') || 'العلامة المستحقة'}: <span className="font-bold">{Number(answer.userMark).toFixed(1)}</span> / <span className="font-bold">{answer.maxMark}</span>
                        </span>
                        <span className="badge badge-pill badge-light font-weight-bold">
                            {showCorrectAnswer ? '🎯 عرض الإجابة النموذجية' : pct === 100 ? '✅ إجابة صحيحة كاملة' : pct > 0 ? '⚠️ إجابة جزئية' : '❌ بدون علامة'}
                        </span>
                    </div>
                </div>

                {/* Linked Skills Badges */}
                {Array.isArray(answer.skills) && answer.skills.length > 0 && (
                    <div className="d-flex flex-wrap align-items-center mt-2" style={{ gap: '0.35rem' }}>
                        <span className="text-muted small" style={{ fontSize: '0.72rem' }}>
                            <i className="fas fa-lightbulb text-warning ml-1"></i>
                            {t('Skills') || 'المهارات المرتبطة'}:
                        </span>
                        {answer.skills.map((s, sIdx) => {
                            const sName = typeof s === 'string' ? s : (s.SkillName || s.Name || s.TopicName);
                            if (!sName) return null;
                            return (
                                <span
                                    key={`q-skill-${sIdx}`}
                                    className="badge badge-light border text-secondary"
                                    style={{ fontSize: '0.7rem', fontWeight: 600, padding: '0.25rem 0.5rem', borderRadius: '0.35rem' }}
                                >
                                    {sName}
                                </span>
                            );
                        })}
                    </div>
                )}

                {isUnanswered && !showCorrectAnswer && (
                    <div className="p-2 border-left-4 rounded mt-2" role="alert" style={{ background: '#fee2e2', borderRight: '4px solid #ef4444', color: '#991b1b', fontSize: '0.8rem' }}>
                        <span>{t('Pages.Exams.NoAnswer') || 'لم يتم حل هذا السؤال'} : <span className="font-weight-bold">0</span> / <span className="font-weight-bold">{answer.maxMark}</span></span>
                    </div>
                )}
            </div>
        </>
    );
}
